import type { ReactElement, ReactNode } from "react"
import { Link } from "gatsby"
import AccentHeadingAtm from "@onestore/hel/dist/components/atoms/AccentHeadingAtm"
import LabelAtm from "@onestore/hel/dist/components/atoms/LabelAtm"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import ConditionalWrapper from "@onestore/hel/dist/helpers/ConditionalWrapper"
import BadgeUds from "@gatsby-plugin-generic-page/components/BadgeUds"
import Countdown from "@gatsby-plugin-generic-page/components/Countdown"
import type {
  BadgesUdsComponent,
  Header as HeaderType,
  TextTypographyBreakpoint,
  TextTypographySingle,
} from "@gatsby-plugin-generic-page/fragments/header"
import type { Label } from "@gatsby-plugin-generic-page/fragments/label"
import { stripMarkdownText } from "@gatsby-plugin-generic-page/helpers/stripMarkdownText"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import { home } from "@ionos-web-design-system/icon/system"
import {
  Breadcrumbs,
  BreadcrumbsItem,
  Icon,
} from "@ionos-web-design-system/react"
import { typeMatches } from "@onestore-graphql"
import UdsThemeProvider from "~/components/UdsThemeProvider"
import { useBreadcrumbsContext } from "~/context/BreadcrumbsContext"
import type { EmbeddedAssetSquidexMetadata } from "~/fragments/image"
import isEmpty from "~/lib/isEmpty"
import type {
  Caption,
  CaptionIcon,
  SquidexMarkdownEmbeddedContent,
} from "~/types/squidex"
import MarkdownCaption from "../Markdown/Caption"
import MarkdownText from "../Markdown/Text"
import MarkdownTitle from "../Markdown/Title"
import Media from "../Media"
import SelectMenu from "../SelectMenu"
import useTextTypography from "./useTextTypography"
import useTitleTypography from "./useTitleTypography"

function Header({
  caption,
  captionTextOverrides,
  title,
  titleTypography,
  cmsTitleTypography,
  text,
  textTypography,
  cmsTextTypography,
  textOverrides,
  textTopSpace,
  titleOverrides,
  hasAccentColor,
  align,
  selectMenuOptions,
  countdown,
  isHorizontalGrid,
  hasBreadcrumbs,
}: HeaderType): ReactElement<HeaderType> {
  const isHeaderTitleH1 = !isEmpty(title) && /^#(?!#)/.test(title)
  const breadcrumbs = useBreadcrumbsContext()
  let icon: EmbeddedAssetSquidexMetadata[] | undefined
  let captionText: string | undefined
  let label: ReactNode
  let badgesList: ReactNode

  const customTitleElementTypography = useTitleTypography(cmsTitleTypography)
  const customTextElementTypography = useTextTypography(cmsTextTypography)

  const hasTextAsString = !isEmpty(text) && typeof text === "string"
  const hasTextAsEmbededContent =
    typeof text === "object" &&
    !isEmpty((text as SquidexMarkdownEmbeddedContent)?.text)

  if (caption) {
    if (typeMatches(caption.__typename, SquidexComponentType.CAPTION)) {
      const { text } = caption as Caption

      captionText = text
    }

    if (typeMatches(caption.__typename, SquidexComponentType.CAPTION_ICON)) {
      const { captionIcon } = caption as CaptionIcon

      icon = captionIcon
    }

    if (typeMatches(caption.__typename, SquidexComponentType.LABEL)) {
      const { text, variant } = caption as Label

      label = (
        <PushOrg bottomSpace={1}>
          <LabelAtm variant={variant || "default"} size="small">
            {text}
          </LabelAtm>
        </PushOrg>
      )
    }

    if (typeMatches(caption.__typename, SquidexComponentType.BADGES_UDS)) {
      const { badges } = caption as BadgesUdsComponent

      badgesList = !isEmpty(badges) ? (
        <div className="flex flex-wrap gap-2 mb-2">
          {badges.map(({ flatData }, index) => (
            <BadgeUds key={index} flatData={flatData} />
          ))}
        </div>
      ) : null
    }
  }

  const titleElement = !isEmpty(title) ? (
    <MarkdownTitle
      typography={
        customTitleElementTypography ||
        titleTypography || {
          small: isHeaderTitleH1 ? "giant2" : "xhuge", // TODO ONESTORE-681
          medium: isHeaderTitleH1 ? "giant2" : "xhuge", // TODO ONESTORE-681
          large: isHeaderTitleH1 ? "giant2" : "xhuge", // TODO ONESTORE-681
        }
      }
      components={titleOverrides}
    >
      {title}
    </MarkdownTitle>
  ) : null

  const textElement =
    hasTextAsString || hasTextAsEmbededContent ? (
      <PushOrg topSpace={isHorizontalGrid ? 0 : (textTopSpace ?? 2)}>
        <MarkdownText
          typography={
            (customTextElementTypography as TextTypographySingle) ||
            textTypography
          }
          typographyBreakpoint={
            customTextElementTypography as TextTypographyBreakpoint
          }
          components={textOverrides}
        >
          {text}
        </MarkdownText>
      </PushOrg>
    ) : null

  return (
    <UdsThemeProvider>
      <ConditionalWrapper
        condition={!isEmpty(align)}
        wrapper={(children) => (
          <AlignContentOrg
            horizontalAlign={{
              large: align?.large.horizontalAlign || "default",
              medium: align?.medium.horizontalAlign || "default",
              small: align?.small.horizontalAlign || "default",
            }}
            horizontalAlignType="both"
          >
            {children}
          </AlignContentOrg>
        )}
      >
        {hasBreadcrumbs && breadcrumbs && !isEmpty(title) ? (
          <div className="mb-4">
            <Breadcrumbs>
              <BreadcrumbsItem asChild>
                <Link to={breadcrumbs.homeUrl}>
                  <Icon icon={home} size="medium" />
                </Link>
              </BreadcrumbsItem>

              {breadcrumbs.items.map((item) =>
                !isEmpty(item.url) ? (
                  <BreadcrumbsItem key={item.url} asChild>
                    <Link to={item.url} title={item.label}>
                      {item.label}
                    </Link>
                  </BreadcrumbsItem>
                ) : (
                  <BreadcrumbsItem key={`ancestor-${item.label}`}>
                    {item.label}
                  </BreadcrumbsItem>
                )
              )}

              <BreadcrumbsItem maxWidth={200}>
                {breadcrumbs.currentLabel ?? stripMarkdownText(title)}
              </BreadcrumbsItem>
            </Breadcrumbs>
          </div>
        ) : null}

        {!isEmpty(selectMenuOptions) ? (
          <PushOrg
            bottomSpace={{
              large: 3.5,
              medium: 3.5,
              small: 7,
            }}
          >
            <SelectMenu options={selectMenuOptions} />
          </PushOrg>
        ) : null}

        <ConditionalWrapper
          condition={!!hasAccentColor}
          wrapper={(children) => (
            <AccentHeadingAtm>
              <div>{children}</div>
            </AccentHeadingAtm>
          )}
        >
          {!isEmpty(countdown) ? (
            <PushOrg bottomSpace={5}>
              <Countdown
                labelTypography="small3"
                digitTypography={{
                  small: "small3",
                  medium: "large3",
                  large: "large3",
                }}
                {...countdown}
              />
            </PushOrg>
          ) : null}

          {!isEmpty(icon) ? (
            <PushOrg bottomSpace={1.5}>
              <AlignContentOrg
                horizontalAlign={{
                  large: align?.large.horizontalAlign || "default",
                  medium: align?.medium.horizontalAlign || "default",
                  small: align?.small.horizontalAlign || "default",
                }}
                horizontalAlignType="both"
              >
                <Media media={icon} />
              </AlignContentOrg>
            </PushOrg>
          ) : null}

          {label}

          {badgesList}

          {!isEmpty(captionText) ? (
            <MarkdownCaption components={captionTextOverrides}>
              {captionText}
            </MarkdownCaption>
          ) : null}

          {isHorizontalGrid ? (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 gap-5">
              <div>{titleElement}</div>

              <div>{textElement}</div>
            </div>
          ) : (
            <>
              {titleElement}

              {textElement}
            </>
          )}
        </ConditionalWrapper>
      </ConditionalWrapper>
    </UdsThemeProvider>
  )
}

export default Header
